import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Export to Excel
export const exportToExcel = (data, filename) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// Export to CSV
export const exportToCSV = (data, filename) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const csv = XLSX.utils.sheet_to_csv(worksheet);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// Export to Markdown
export const exportToMarkdown = (data, filename, title) => {
  let md = `# ${title}\n\n`;
  md += `*Exported on ${new Date().toLocaleString()}*\n\n`;
  
  if (data.length === 0) {
    md += 'No data to export.\n';
  } else {
    // Create table header
    const headers = Object.keys(data[0]);
    md += '| ' + headers.join(' | ') + ' |\n';
    md += '| ' + headers.map(() => '---').join(' | ') + ' |\n';
    
    // Add rows
    data.forEach(row => {
      md += '| ' + headers.map(h => (row[h] || '').toString().replace(/\|/g, '\\|')).join(' | ') + ' |\n';
    });
  }
  
  const blob = new Blob([md], { type: 'text/markdown;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.md`;
  link.click();
  URL.revokeObjectURL(url);
};

// Export to PDF with improved formatting
export const exportToPDF = (data, filename, title) => {
  const doc = new jsPDF('landscape'); // Changed to landscape
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header - Company Name
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(67, 56, 202); // Purple color
  doc.text('Wixon & Cross Desk Suite', pageWidth / 2, 20, { align: 'center' });
  
  // Title
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.text(title, pageWidth / 2, 30, { align: 'center' });
  
  // Export date
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100, 100, 100);
  const exportDate = `Exported on ${new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })}`;
  doc.text(exportDate, pageWidth / 2, 36, { align: 'center' });
  
  // Line separator
  doc.setDrawColor(200, 200, 200);
  doc.line(14, 40, pageWidth - 14, 40);
  
  if (data.length > 0) {
    const headers = Object.keys(data[0]);
    const rows = data.map(row => headers.map(h => {
      const value = row[h] || '';
      // Don't truncate in landscape - more space available
      return value.toString();
    }));
    
    doc.autoTable({
      head: [headers],
      body: rows,
      startY: 45,
      styles: { 
        fontSize: 8, 
        cellPadding: 2,
        overflow: 'linebreak',
        cellWidth: 'auto'
      },
      headStyles: { 
        fillColor: [67, 56, 202], 
        textColor: 255,
        fontStyle: 'bold',
        halign: 'left'
      },
      alternateRowStyles: { 
        fillColor: [248, 248, 250] 
      },
      margin: { top: 45, left: 14, right: 14 },
      didDrawPage: (data) => {
        // Footer with page numbers
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }
    });
  } else {
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('No data to export', pageWidth / 2, 55, { align: 'center' });
  }
  
  doc.save(`${filename}.pdf`);
};

// Export to Text
export const exportToText = (data, filename, title) => {
  let text = `${title}\n${'='.repeat(title.length)}\n`;
  text += `Exported on ${new Date().toLocaleString()}\n\n`;
  
  if (data.length === 0) {
    text += 'No data to export.\n';
  } else {
    data.forEach((item, idx) => {
      text += `\n[${idx + 1}] ${'-'.repeat(60)}\n`;
      Object.entries(item).forEach(([key, value]) => {
        text += `  ${key}: ${value || 'N/A'}\n`;
      });
    });
  }
  
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.txt`;
  link.click();
  URL.revokeObjectURL(url);
};

// Format data for export
export const formatDataForExport = (items, type = 'defect') => {
  return items.map(item => {
    if (type === 'defect') {
      return {
        'ID': item.id.substring(0, 8),
        'Application': item.applicationName || 'N/A',
        'Title': item.title,
        'Status': item.status,
        'Priority': item.priority,
        'Test Step': item.testStep || '',
        'Expected Result': item.expectedResult || '',
        'Actual Result': item.actualResult || '',
        'Created By': item.createdByEmail,
        'Assigned (Fix)': item.assignedToFix || 'Unassigned',
        'Assigned (Test)': item.assignedToTest || 'Unassigned',
        'Created': item.createdAt?.toDate ? item.createdAt.toDate().toLocaleString() : new Date(item.createdAt).toLocaleString()
      };
    } else if (type === 'feature') {
      return {
        'ID': item.id.substring(0, 8),
        'Application': item.applicationName || 'N/A',
        'Title': item.title,
        'Status': item.status,
        'Priority': item.priority,
        'Votes': item.voteCount || 0,
        'Description': item.description || '',
        'Requested By': item.createdByEmail,
        'Developer': item.assignedToDev || 'Unassigned',
        'Tester': item.assignedToTest || 'Unassigned',
        'Created': item.createdAt?.toDate ? item.createdAt.toDate().toLocaleString() : new Date(item.createdAt).toLocaleString()
      };
    } else if (type === 'note') {
      return {
        'ID': item.id.substring(0, 8),
        'Title': item.title,
        'Content': item.content || '',
        'Created By': item.createdByEmail,
        'Shared With': (item.sharedWith || []).length + ' users',
        'Created': item.createdAt?.toDate ? item.createdAt.toDate().toLocaleString() : new Date(item.createdAt).toLocaleString(),
        'Updated': item.updatedAt?.toDate ? item.updatedAt.toDate().toLocaleString() : new Date(item.updatedAt).toLocaleString()
      };
    }
    return item;
  });
};
